import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { doc, updateDoc, increment, collection, addDoc, getDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { useDummyData } from './DummyDataContext';
import { useAuth } from './AuthContext';

const CreditsContext = createContext(null);

export function CreditsProvider({ children }) {
  const [credits, setCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { isDummyDataEnabled } = useDummyData();
  const { user } = useAuth();

  useEffect(() => {
    if (isDummyDataEnabled) {
      setIsLoading(false);
      return;
    }

    let unsubscribe = () => {};

    if (user) {
      const userRef = doc(db, 'users', user.uid);
      
      // Set up real-time listener for credits
      unsubscribe = onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          setCredits(doc.data()?.credits || 0);
        }
        setIsLoading(false);
      }, (error) => {
        console.error('Error listening to credits:', error);
        setIsLoading(false);
      });
    } else {
      setCredits(0);
      setIsLoading(false);
    }

    return () => unsubscribe();
  }, [user, isDummyDataEnabled]);

  const updateCredits = useCallback(async (amount, packagePrice = null) => {
    if (!user) return false;

    try {
      setIsLoading(true);

      if (isDummyDataEnabled) {
        setCredits(prev => prev + amount);
        return true;
      }

      const batch = writeBatch(db);
      const userRef = doc(db, 'users', user.uid);
      const transactionRef = doc(collection(db, 'transactions'));
      
      // Update user's credit balance
      batch.update(userRef, {
        credits: increment(amount)
      });

      // Create transaction record
      const transactionData = {
        userId: user.uid,
        action: amount > 0 ? 'credit_purchase' : 'image_generation',
        type: amount > 0 ? 'credit' : 'debit',
        amount,
        timestamp: new Date(),
        status: 'completed'
      };

      // Add package price if this is a purchase
      if (amount > 0 && packagePrice) {
        transactionData.price = packagePrice;
      }

      batch.set(transactionRef, transactionData);

      // Commit both operations atomically
      await batch.commit();

      return true;
    } catch (error) {
      console.error('Error updating credits:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, isDummyDataEnabled]);

  const checkCredits = useCallback(async (requiredAmount) => {
    if (!user) return false;

    try {
      if (isDummyDataEnabled) {
        return credits >= requiredAmount;
      }

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      return (userDoc.exists() && (userDoc.data()?.credits || 0) >= requiredAmount);
    } catch (error) {
      console.error('Error checking credits:', error);
      return false;
    }
  }, [user, credits, isDummyDataEnabled]);

  const value = {
    credits,
    isLoading,
    updateCredits,
    checkCredits
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}